import React, { Component } from 'react';
import { ZoomIn } from 'react-feather';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { XIcon } from '@components-library/Icons/iconsComponents';
import { IDocumentViewProps, IDocumentViewState } from '@components-shared/DocumentsViewer/interfaces';
import { CONSTANTS } from '@components-shared/constants';
import ModalPortal from '@components-shared/modal/ModalPortal';
import Preloader from '@components-shared/preloader';
import { keyMap, KeyMapKeys } from '@locales/lokalise/keyMap';
import cn from 'classnames';
import { connectToContext } from 'contextStore';
import debounce from 'lodash/debounce';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const DOCUMENT_WIDTH_SMALL_SIZE = 390;
const DOCUMENT_WIDTH_FULL_SIZE = 900;
const PDF_VIEWER_PADDING = 90;
const PDS_OPTION = {
  isEvalSupported: false,
};

class DocumentView extends Component<IDocumentViewProps, IDocumentViewState> {
  file = {
    url: this.props.src,
    withCredentials: true,
  };

  state: IDocumentViewState = {
    originalSrc: '',
    originalDocProcessing: false,
    processing: true,
    numPages: 1,
    errorImg: false,
    passwordError: false,
    documentCustomWidth: window.innerWidth - PDF_VIEWER_PADDING,
  };

  handleResize = debounce(() => {
    this.setState({
      documentCustomWidth: window.innerWidth - PDF_VIEWER_PADDING,
    });
  }, 500);

  componentDidMount() {
    if (this.props.useCustomFullSize) {
      window.addEventListener('resize', this.handleResize);
    }
  }

  componentWillUnmount() {
    if (this.props.useCustomFullSize) {
      window.removeEventListener('resize', this.handleResize);
    }
  }

  showOriginal = (src: string, state = false): void => {
    this.setState({ originalSrc: src, originalDocProcessing: state, errorImg: false });
  };

  onDocumentLoadSuccess = ({ numPages }: { numPages: number }): void => {
    this.resolveDocumentUpload();
    this.setState({ numPages, processing: false });
  };

  renderImage = (src: string): JSX.Element => {
    const { i18next } = this.props;
    const { processing, originalDocProcessing, errorImg } = this.state;
    const isRenderImage = this.props.isRenderImage ?? true;

    const isProcessing = processing || originalDocProcessing;

    return (
      <div className="document-view__img">
        {isProcessing && <Preloader />}
        {errorImg && <span>{i18next.t(keyMap.sharedErrorsFailedToLoadFile)}</span>}
        {isRenderImage && (
          <img
            src={src}
            style={isProcessing ? { visibility: 'hidden' } : {}}
            className="document-modal__img"
            alt=""
            onLoad={() => {
              this.resolveDocumentUpload();

              this.setState({
                processing: false,
                originalDocProcessing: false,
              });
            }}
            onError={() => {
              this.rejectDocumentUpload();

              this.setState({
                errorImg: true,
                processing: false,
                originalDocProcessing: false,
              });
            }}
          />
        )}
      </div>
    );
  };

  onPassword = (): void => {
    this.resolveDocumentUpload();
    this.setState({ passwordError: true });
  };

  resolveDocumentUpload = (): void => {
    if (this.props.resolveDocumentItemUploadPromise) {
      this.props.resolveDocumentItemUploadPromise();
    }
  };

  rejectDocumentUpload = (): void => {
    if (this.props.rejectDocumentItemUploadPromise) {
      this.props.rejectDocumentItemUploadPromise();
    }
  };

  renderPDF = (src: string, width: number, allPages: boolean): JSX.Element => {
    const { i18next } = this.props;
    const { numPages, passwordError } = this.state;
    const pages = [];

    for (let i = 1; i <= numPages; i += 1) {
      pages.push(i);
    }

    if (passwordError) {
      return <div className="document-view__img">{i18next.t(keyMap.documentViewerPasswordError)}</div>;
    }

    return (
      <div className="document-view__img">
        <Document
          file={this.file}
          onLoadSuccess={this.onDocumentLoadSuccess}
          onPassword={this.onPassword}
          onLoadError={this.rejectDocumentUpload}
          loading={<Preloader />}
          options={PDS_OPTION}
          error={i18next.t(keyMap.documentViewerLoadingError)}>
          {allPages ? (
            pages.map((page) => <Page loading="" pageNumber={page} key={page} width={width} />)
          ) : (
            <Page pageNumber={1} width={width} height={400} />
          )}
        </Document>
      </div>
    );
  };

  renderOriginalDocument = (): JSX.Element => {
    const { originalSrc, documentCustomWidth } = this.state;
    const { useCustomFullSize } = this.props;

    const fullSizeWidth =
      useCustomFullSize && documentCustomWidth <= DOCUMENT_WIDTH_FULL_SIZE
        ? documentCustomWidth
        : DOCUMENT_WIDTH_FULL_SIZE;

    if (!originalSrc) {
      return <></>;
    }

    return (
      <ModalPortal>
        <div className="document-modal">
          <div role="presentation" className="document-modal__overlay" onClick={() => this.showOriginal('')} />
          <button type="button" className="document-modal__close" onClick={() => this.showOriginal('')} title="Close">
            <XIcon size={20} />
          </button>

          <div className="document-modal__inner document-modal--view">
            {this.renderDocumentItem(originalSrc, originalSrc, fullSizeWidth, true)}
          </div>
        </div>
      </ModalPortal>
    );
  };

  getSrcForImage = (): string => {
    const { src, isSmallImageCreated } = this.props;

    if (!isSmallImageCreated) {
      return src;
    }

    return `${src}&isSmall=true`;
  };

  renderDocumentItem = (imageSrc: string, pdfSrc: string, width: number, allPages: boolean): JSX.Element => {
    const { fileType } = this.props;

    return fileType === CONSTANTS.FILE_TYPE.PDF ? this.renderPDF(pdfSrc, width, allPages) : this.renderImage(imageSrc);
  };

  render(): JSX.Element {
    const { fileType, src, withoutDocumentPreview, buttonCustomTitle, className, classes, i18next, tooltip } =
      this.props;

    if (!src || !fileType) {
      return <></>;
    }

    const { errorImg } = this.state;

    const buttonBody = buttonCustomTitle ? (
      buttonCustomTitle
    ) : (
      <>
        <ZoomIn size={13} />
        {i18next.t(keyMap.documentOpenFullSize)}
      </>
    );

    return (
      <div className={cn('document-view__item', classes?.documentViewItem)}>
        {!withoutDocumentPreview &&
          this.renderDocumentItem(this.getSrcForImage(), src, DOCUMENT_WIDTH_SMALL_SIZE, false)}
        {this.renderOriginalDocument()}

        {!errorImg && (
          <button
            type="button"
            className={cn('link-btn', className)}
            onClick={() => this.showOriginal(src, true)}
            title={tooltip && i18next.t(tooltip as KeyMapKeys)}>
            {buttonBody}
          </button>
        )}
      </div>
    );
  }
}

const DocumentViewWithContext = connectToContext(DocumentView);

export { DocumentViewWithContext as DocumentView };
